import React, { ReactElement, useEffect, useRef, useState } from 'react';
import useCountDown from 'react-countdown-hook';
import { IRecordingStateReducerAction } from '../../hooks';
import { IRecordingState } from '../../models';
import CountdownState from './CountdownState';
import styles from './Record.scss';

interface RecordProp {
  state: IRecordingState;
  dispatch: React.Dispatch<IRecordingStateReducerAction>;
}

export default function Record(props: RecordProp): ReactElement {
  const { state } = props;
  const recorder = state.recorder;
  const videoElement = useRef<HTMLVideoElement>(null);
  const canvasElement = useRef<HTMLCanvasElement>(null);
  const imageElement = useRef<HTMLImageElement>(null);
  const [countdownState, setCountdownState] = useState<CountdownState>(CountdownState.Initialize);
  const [timeLeft, { start }] = useCountDown(3000, 1000);

  /**
   * Attaches the media stream to the video ref
   */
  useEffect(() => {
    if (recorder && videoElement.current) {
      const video: HTMLMediaElementWithCaptureStream =
        videoElement.current as unknown as HTMLMediaElementWithCaptureStream;
      video.srcObject = recorder.stream;
    }
  }, [recorder, videoElement, imageElement, canvasElement]);

  /**
   * Starts the countdown
   */
  useEffect(() => {
    start();
    setCountdownState(CountdownState.Countdown);
  }, [start]);

  /**
   * Remove the countdown overlay
   */
  useEffect(() => {
    if (countdownState === CountdownState.Countdown && timeLeft === 0) {
      setCountdownState(CountdownState.Complete);
    }
  }, [countdownState, timeLeft]);

  useEffect(() => {
    if (recorder && countdownState === CountdownState.Complete) {
      recorder.startRecording();
    }
  }, [countdownState, recorder]);

  return (
    <div className={styles.recordingContainer} data-testid="record-container">
      {timeLeft > 0 && (
        <>
          <div className={styles.greyBackground} data-testid="grey-background"></div>
          <div className={styles.countdown} data-testid="countdown">
            {timeLeft / 1000}
          </div>
        </>
      )}
      <div className={styles.videoRecorderBackground}>
        <div className={styles.videoRecorderContainer}>
          <video
            id="video"
            data-testid="video-recorder"
            className={styles.videoRecorder}
            autoPlay
            muted
            ref={videoElement}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * TypeScript interface doesn't yet support the capture stream methods, so we interface them out here
 */
interface HTMLMediaElementWithCaptureStream extends HTMLMediaElement {
  captureStream(): MediaStream;
  mozCaptureStream(): MediaStream;
}
